/**
 * High-Performance Color Math Utilities
 * 
 * Pure math functions for color conversion and manipulation.
 * These functions are designed for pixel-level operations where
 * tinycolor overhead would cause performance issues.
 * 
 * CRITICAL: These functions are called millions of times per image.
 * Every micro-optimization matters here.
 * 
 * @module lib/colorMath
 */

// ============================================================================
// Types
// ============================================================================

export interface HSL {
  h: number;  // 0-360
  s: number;  // 0-1
  l: number;  // 0-1
}

export interface RGB {
  r: number;  // 0-255
  g: number;  // 0-255
  b: number;  // 0-255
}

export interface PrecomputedDominant {
  hex: string;
  hsl: HSL;
  rgb: RGB;
  tolerance: number;
  satTolerance: number; // tolerance * 3 for saturation
}

export interface PrecomputedExclude {
  hex: string;
  rgb: RGB;
  tolerance: number;
}

export interface PrecomputedConfig {
  dominants: PrecomputedDominant[];
  excludes: PrecomputedExclude[];
  targetHSL: HSL;
  minSat: number;
  maxSat: number;
  hslAdjust: {
    saturation: number;
    lightness: number;
    vividness: number;
  };
}

// ============================================================================
// RGB ↔ HSL Conversion (Pure Math - No Libraries)
// ============================================================================

/**
 * Convert RGB to HSL
 * Optimized for tight loops - no object creation overhead
 */
export function rgbToHsl(r: number, g: number, b: number): HSL {
  // Normalize to 0-1
  const rN = r / 255;
  const gN = g / 255;
  const bN = b / 255;

  const max = Math.max(rN, gN, bN);
  const min = Math.min(rN, gN, bN);
  const l = (max + min) / 2;
  
  // Achromatic (gray)
  if (max === min) {
    return { h: 0, s: 0, l };
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  
  let h: number;
  switch (max) {
    case rN:
      h = ((gN - bN) / d + (gN < bN ? 6 : 0)) / 6;
      break;
    case gN:
      h = ((bN - rN) / d + 2) / 6;
      break;
    default: // bN
      h = ((rN - gN) / d + 4) / 6;
      break;
  }

  return { h: h * 360, s, l };
}

/**
 * Convert HSL to RGB
 * Optimized for tight loops
 */
export function hslToRgb(h: number, s: number, l: number): RGB {
  // Achromatic (gray)
  if (s === 0) {
    const gray = Math.round(l * 255);
    return { r: gray, g: gray, b: gray };
  }

  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hNorm = h / 360; // Normalize to 0-1

  return {
    r: Math.round(hue2rgb(p, q, hNorm + 1/3) * 255),
    g: Math.round(hue2rgb(p, q, hNorm) * 255),
    b: Math.round(hue2rgb(p, q, hNorm - 1/3) * 255)
  };
}

/**
 * Convert hex color to RGB
 * Handles both #RRGGBB and #RGB formats
 */
export function hexToRgb(hex: string): RGB | null {
  // Remove # if present
  const clean = hex.replace(/^#/, '');
  
  let r: number, g: number, b: number;
  
  if (clean.length === 3) {
    // Short format: #RGB -> #RRGGBB
    r = parseInt(clean[0] + clean[0], 16);
    g = parseInt(clean[1] + clean[1], 16);
    b = parseInt(clean[2] + clean[2], 16);
  } else if (clean.length === 6) {
    r = parseInt(clean.substring(0, 2), 16);
    g = parseInt(clean.substring(2, 4), 16);
    b = parseInt(clean.substring(4, 6), 16);
  } else {
    return null;
  }
  
  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    return null;
  }
  
  return { r, g, b };
}

/**
 * Convert hex color to HSL
 */
export function hexToHsl(hex: string): HSL | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return rgbToHsl(rgb.r, rgb.g, rgb.b);
}

/**
 * Convert RGB to hex string
 */
export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => {
    const clamped = Math.max(0, Math.min(255, Math.round(n)));
    return clamped.toString(16).padStart(2, '0');
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

// ============================================================================
// Pixel Decision Functions (Hot Path - Heavily Optimized)
// ============================================================================

/**
 * Check if pixel should be transformed based on precomputed config
 * This is the HOT PATH - called for every pixel in an image
 * 
 * @returns Object with shouldTransform and matchedDominant
 */
export function shouldTransformPixelFast(
  r: number,
  g: number,
  b: number,
  config: PrecomputedConfig
): { shouldTransform: boolean; matchedDominant: string | null; tolerance: number } {
  // Get pixel HSL (inline computation, no function call overhead)
  const rN = r / 255;
  const gN = g / 255;
  const bN = b / 255;

  const max = Math.max(rN, gN, bN);
  const min = Math.min(rN, gN, bN);
  const l = (max + min) / 2;
  
  // Fast reject: very dark or very light pixels
  if (l < 0.05 || l > 0.95) {
    return { shouldTransform: false, matchedDominant: null, tolerance: 0 };
  }
  
  // Calculate saturation only if needed
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  }
  
  // Fast reject: saturation out of range
  if (s < config.minSat || s > config.maxSat) {
    return { shouldTransform: false, matchedDominant: null, tolerance: 0 };
  }
  
  // Check exclude colors (RGB distance check - very fast)
  for (let i = 0; i < config.excludes.length; i++) {
    const ex = config.excludes[i];
    const rDiff = Math.abs(r - ex.rgb.r);
    const gDiff = Math.abs(g - ex.rgb.g);
    const bDiff = Math.abs(b - ex.rgb.b);
    
    if (rDiff < ex.tolerance && gDiff < ex.tolerance && bDiff < ex.tolerance) {
      return { shouldTransform: false, matchedDominant: null, tolerance: 0 };
    }
  }
  
  // Calculate hue only if we passed all fast-reject checks
  let h = 0;
  if (max !== min) {
    const d = max - min;
    switch (max) {
      case rN:
        h = ((gN - bN) / d + (gN < bN ? 6 : 0)) / 6;
        break;
      case gN:
        h = ((bN - rN) / d + 2) / 6;
        break;
      default:
        h = ((rN - gN) / d + 4) / 6;
        break;
    }
    h *= 360;
  }
  
  // Check dominant colors (hue distance check)
  for (let i = 0; i < config.dominants.length; i++) {
    const dom = config.dominants[i];
    
    // Hue distance (circular)
    let hueDiff = Math.abs(h - dom.hsl.h);
    if (hueDiff > 180) hueDiff = 360 - hueDiff;
    
    if (hueDiff <= dom.tolerance) {
      // Saturation check (already computed s above)
      const satDiff = Math.abs(s - dom.hsl.s) * 100;
      if (satDiff <= dom.satTolerance) {
        return { 
          shouldTransform: true, 
          matchedDominant: dom.hex, 
          tolerance: dom.tolerance 
        };
      }
    }
  }
  
  return { shouldTransform: false, matchedDominant: null, tolerance: 0 };
}

/**
 * Transform pixel to target color using precomputed config
 * This is the HOT PATH - called for every matching pixel
 */
export function transformPixelFast(
  r: number,
  g: number,
  b: number,
  config: PrecomputedConfig
): RGB {
  // Get pixel HSL
  const pixelHSL = rgbToHsl(r, g, b);
  
  // Base saturation calculation (70% target + 30% pixel)
  const baseSat = pixelHSL.s * 0.3 + config.targetHSL.s * 0.7;
  
  // Apply saturation adjustment (-100 to +100 → -1 to +1)
  let newSat = baseSat + (config.hslAdjust.saturation / 100);
  
  // Apply vividness curve
  if (config.hslAdjust.vividness !== 0) {
    const vividFactor = config.hslAdjust.vividness / 100;
    newSat = newSat + (newSat * vividFactor * pixelHSL.s);
  }
  newSat = Math.max(0, Math.min(1, newSat));
  
  // Preserve pixel lightness with optional adjustment
  let newLight = pixelHSL.l + (config.hslAdjust.lightness / 100);
  newLight = Math.max(0.02, Math.min(0.98, newLight));
  
  // Use target hue (brand color)
  return hslToRgb(config.targetHSL.h, newSat, newLight);
}

// ============================================================================
// Pre-computation Functions (Called Once Before Pixel Loop)
// ============================================================================

/**
 * Precompute config for fast pixel processing
 * Call this ONCE before processing an image, not per-pixel!
 */
export function precomputeColorConfig(
  targetColor: string,
  dominantColors: Array<{ hex: string; tolerance: number }>,
  excludeColors: Array<string | { hex: string; tolerance?: number }>,
  hslAdjust: { saturation: number; lightness: number; vividness: number },
  minSatThreshold: number = 0.08,
  maxSatThreshold: number = 1.0
): PrecomputedConfig | null {
  // Pre-compute target HSL
  const targetHSL = hexToHsl(targetColor);
  if (!targetHSL) {
    console.error('[colorMath] Invalid target color:', targetColor);
    return null;
  }
  
  // Pre-compute dominant colors
  const dominants: PrecomputedDominant[] = [];
  for (const dom of dominantColors) {
    const rgb = hexToRgb(dom.hex);
    const hsl = hexToHsl(dom.hex);
    if (rgb && hsl) {
      dominants.push({
        hex: dom.hex.toUpperCase(),
        rgb,
        hsl,
        tolerance: dom.tolerance,
        satTolerance: dom.tolerance * 3
      });
    }
  }
  
  // Pre-compute exclude colors
  const excludes: PrecomputedExclude[] = [];
  for (const ex of excludeColors) {
    const hex = typeof ex === 'string' ? ex : ex.hex;
    const tolerance = typeof ex === 'string' ? 15 : (ex.tolerance ?? 15);
    const rgb = hexToRgb(hex);
    if (rgb) {
      excludes.push({ hex: hex.toUpperCase(), rgb, tolerance });
    }
  }
  
  return {
    dominants,
    excludes,
    targetHSL,
    minSat: minSatThreshold,
    maxSat: maxSatThreshold,
    hslAdjust
  };
}

// ============================================================================
// Color Similarity Checks (Optimized for Object-Level Operations)
// ============================================================================

/**
 * Check if two colors have similar hue
 * Optimized version that doesn't use tinycolor
 */
export function hasSimilarHueFast(color1: string, color2: string, tolerance: number = 15): boolean {
  const hsl1 = hexToHsl(color1);
  const hsl2 = hexToHsl(color2);
  
  if (!hsl1 || !hsl2) return false;
  
  // Skip comparison for very desaturated colors
  // Treat very desaturated colors or very dark/very light colors as neutral
  // and do not consider them similar by hue. This prevents near-black
  // fills (which are essentially neutral in practice) from being treated
  // as similar to a vivid dominant color.
  if (hsl1.s < 0.1 || hsl2.s < 0.1) return false;
  // Also ignore comparisons when either color is very dark or very light
  // (lightness threshold chosen to avoid transforming near-black text)
  if (hsl1.l < 0.12 || hsl2.l < 0.12 || hsl1.l > 0.95 || hsl2.l > 0.95) return false;
  
  let hueDiff = Math.abs(hsl1.h - hsl2.h);
  if (hueDiff > 180) hueDiff = 360 - hueDiff;
  const similar = hueDiff <= tolerance;

  // Helpful debug output when diagnosing surprising matches
  if (process.env.NODE_ENV === 'development') {
    console.debug(`[hasSimilarHueFast] ${color1} -> hsl(${hsl1.h.toFixed(1)}, ${hsl1.s.toFixed(2)}, ${hsl1.l.toFixed(2)}) vs ${color2} -> hsl(${hsl2.h.toFixed(1)}, ${hsl2.s.toFixed(2)}, ${hsl2.l.toFixed(2)}) | hueDiff=${hueDiff.toFixed(1)} tolerance=${tolerance} similar=${similar}`);
  }

  return similar;
}

/**
 * Check if a color is neutral (grayscale, near-white, or near-black)
 * Optimized version that doesn't use tinycolor
 */
export function isNeutralColorFast(color: string): boolean {
  const hsl = hexToHsl(color);
  if (!hsl) return false;
  
  // Consider it neutral if:
  // 1. Saturation is very low (grayscale)
  // 2. Lightness is very high (near white) - above 95%
  // 3. Lightness is very low (near black) - below 5%
  return hsl.s < 0.1 || hsl.l > 0.95 || hsl.l < 0.05;
}

/**
 * Parse any color format (hex, rgb, rgba, named) to RGB
 * Uses a canvas context for complex formats
 */
let colorParseCanvas: HTMLCanvasElement | null = null;
let colorParseCtx: CanvasRenderingContext2D | null = null;

export function parseColorToRgb(color: string): RGB | null {
  // Fast path for hex colors
  if (color.startsWith('#')) {
    return hexToRgb(color);
  }
  
  // Fast path for rgb/rgba format
  const rgbMatch = color.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1], 10),
      g: parseInt(rgbMatch[2], 10),
      b: parseInt(rgbMatch[3], 10)
    };
  }
  
  // Slow path: use canvas for named colors and other formats
  if (!colorParseCanvas) {
    colorParseCanvas = document.createElement('canvas');
    colorParseCanvas.width = 1;
    colorParseCanvas.height = 1;
    colorParseCtx = colorParseCanvas.getContext('2d', { willReadFrequently: true });
  }
  
  if (!colorParseCtx) return null;
  
  colorParseCtx.fillStyle = color;
  colorParseCtx.fillRect(0, 0, 1, 1);
  const pixel = colorParseCtx.getImageData(0, 0, 1, 1).data;
  
  return { r: pixel[0], g: pixel[1], b: pixel[2] };
}

/**
 * Convert any color format to hex
 */
export function parseColorToHex(color: string): string | null {
  const rgb = parseColorToRgb(color);
  if (!rgb) return null;
  return rgbToHex(rgb.r, rgb.g, rgb.b);
}

// ============================================================================
// Memoization Cache for Repeated Color Operations
// ============================================================================

const hslCache = new Map<string, HSL | null>();
const MAX_CACHE_SIZE = 500;

/**
 * Get HSL with caching (for object-level operations, not pixel-level)
 */
export function getHslCached(color: string): HSL | null {
  if (hslCache.has(color)) {
    return hslCache.get(color)!;
  }
  
  // Parse color to RGB first
  const rgb = parseColorToRgb(color);
  const hsl = rgb ? rgbToHsl(rgb.r, rgb.g, rgb.b) : null;
  
  // Add to cache (evict oldest if full)
  if (hslCache.size >= MAX_CACHE_SIZE) {
    const firstKey = hslCache.keys().next().value;
    if (firstKey !== undefined) {
      hslCache.delete(firstKey);
    }
  }
  hslCache.set(color, hsl);
  
  return hsl;
}

/**
 * Clear HSL cache (call when processing new image/artwork)
 */
export function clearHslCache(): void {
  hslCache.clear();
}
